import {
  HIGHLIGHT_WORKOUT_QUERY_PARAM,
  PROGRAMME_OVERVIEW_PENDING_HIGHLIGHT_SESSION_KEY,
} from 'src/constants/programme-overview-url'
import { CompletedProgrammeWorkout, ProgrammeWorkout } from 'src/dao/core-dao'
import { deriveProgrammeWorkoutProgress } from 'src/hook/programme-workout-progress-derive'
import { Location, NavigateFunction } from 'react-router-dom'

export type PendingProgrammeOverviewHighlightPayload = {
  programmeId: string
  week: number
  workoutId: string
}

/**
 * After completing a programme workout, persist the next incomplete workout so programme overview can
 * highlight it when the user returns (including via browser back).
 */
export const storePendingProgrammeOverviewHighlight = (
  programmeId: string,
  programmeWorkouts: ProgrammeWorkout[],
  completedProgrammeWorkouts: CompletedProgrammeWorkout[],
  justCompletedProgrammeWorkoutId: string,
): void => {
  if (typeof sessionStorage === 'undefined') {
    return
  }
  const alreadyRecorded = completedProgrammeWorkouts.some((c) => c.programmeWorkout.id === justCompletedProgrammeWorkoutId)
  const optimisticCompleted: CompletedProgrammeWorkout[] = alreadyRecorded
    ? completedProgrammeWorkouts
    : [...completedProgrammeWorkouts, { programmeWorkout: { id: justCompletedProgrammeWorkoutId } } as CompletedProgrammeWorkout]
  const derived = deriveProgrammeWorkoutProgress(programmeWorkouts, optimisticCompleted)
  const week = derived.nextIncompleteWeek
  const workoutId = derived.nextIncompleteWorkoutId
  if (!workoutId || week == null || week < 1) {
    return
  }
  const payload: PendingProgrammeOverviewHighlightPayload = { programmeId, week, workoutId }
  sessionStorage.setItem(PROGRAMME_OVERVIEW_PENDING_HIGHLIGHT_SESSION_KEY, JSON.stringify(payload))
}

/**
 * If the user just finished a workout in this programme, merge week + highlight into the URL once.
 * @returns true if navigation was applied
 */
export const consumePendingProgrammeOverviewHighlight = (
  programmeId: string,
  navigate: NavigateFunction,
  location: Location,
): boolean => {
  if (typeof sessionStorage === 'undefined') {
    return false
  }
  const raw = sessionStorage.getItem(PROGRAMME_OVERVIEW_PENDING_HIGHLIGHT_SESSION_KEY)
  if (!raw) {
    return false
  }
  try {
    const payload = JSON.parse(raw) as PendingProgrammeOverviewHighlightPayload
    if (payload.programmeId !== programmeId || !payload.workoutId) {
      return false
    }
    sessionStorage.removeItem(PROGRAMME_OVERVIEW_PENDING_HIGHLIGHT_SESSION_KEY)
    const params = new URLSearchParams(location.search)
    if (payload.week > 0) {
      params.set('week', String(payload.week))
    }
    params.set(HIGHLIGHT_WORKOUT_QUERY_PARAM, payload.workoutId)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true })
    return true
  } catch {
    return false
  }
}
